import type { UseQueryResult } from "@tanstack/react-query";
import type { UseFormReturn } from "react-hook-form";

import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { Broadcast, GetMyAccountQuery, GetMyProfileQuery, SongsResponse } from "@/api/queries";

import { usePostBroadcast, usePutBroadcast } from "@/api/mutations";
import { sendProperty } from "@/helpers/helpers";

import type { BroadcastFormValues } from "../utils/broadcastSchema";

import { buildBroadcastPayload } from "../utils/broadcastUtils";

type UseBroadcastMutationProps = {
  form: UseFormReturn<BroadcastFormValues>;
  broadcastId: string | undefined;
  broadcasts: UseQueryResult<Broadcast[], Error>;
  refetchBroadcast: () => Promise<unknown>;
  myAccount: GetMyAccountQuery;
  songs: UseQueryResult<SongsResponse, Error>;
  profile: GetMyProfileQuery;
};

type UseBroadcastMutationReturn = {
  pending: boolean;
  mutationErrorMessage: string;
  addBroadcast: () => Promise<void>;
  updateBroadcast: () => Promise<void>;
};

export default function useBroadcastMutation({
  form,
  broadcastId,
  broadcasts,
  refetchBroadcast,
  myAccount,
  songs,
  profile,
}: UseBroadcastMutationProps): UseBroadcastMutationReturn {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [pending, setPending] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");

  const createMutation = usePostBroadcast();
  const updateMutation = usePutBroadcast();

  async function startReqAndCheckValidation(): Promise<boolean> {
    setPending(true);
    form.clearErrors("root");
    createMutation.reset();
    updateMutation.reset();
    const valid = await form.trigger();
    if (!valid)
      return false;
    return true;
  }

  function handleSuccess(message: string): void {
    toast.success(message);
    void navigate({ to: "/broadcasts" });
    void broadcasts.refetch();
    void refetchBroadcast();
    sendProperty({
      property: "broadcast_opp_applicant",
      value: true,
      email: profile.data?.email,
    });
    setPending(false);
  }

  function handleError(error: Error): void {
    setMutationErrorMessage(error.message);
    toast.error(error.message);
    setPending(false);
  }

  const addBroadcast = async (): Promise<void> => {
    const valid = await startReqAndCheckValidation();
    if (!valid) {
      setPending(false);
      return;
    }
    try {
      const response = await createMutation.mutateAsync(
        buildBroadcastPayload(form.getValues(), myAccount.data, songs.data?.works),
      );
      if (response.failedvalidation) {
        form.setError("root", {
          message: response.messages?.join(", ") ?? t("other:validation-error", "Validation error, please try again") ?? "Validation error, please try again",
        });
        setPending(false);
      }
      else {
        handleSuccess(t("other:successfully-added-broadcast-toast", "Broadcast added successfully") ?? "Broadcast added successfully");
      }
    }
    catch (error: unknown) {
      handleError(error instanceof Error ? error : new Error(String(error)));
    }
  };

  const updateBroadcast = async (): Promise<void> => {
    const valid = await startReqAndCheckValidation();
    if (!valid) {
      setPending(false);
      return;
    }
    try {
      const response = await updateMutation.mutateAsync(
        buildBroadcastPayload(form.getValues(), myAccount.data, songs.data?.works, broadcastId),
      );
      if (response.failedvalidation) {
        form.setError("root", {
          message: response.messages?.join(", ") ?? t("other:validation-error", "Validation error, please try again") ?? "Validation error, please try again",
        });
        setPending(false);
      }
      else {
        handleSuccess(t("other:successfully-updated-broadcast-toast", "Broadcast updated successfully") ?? "Broadcast updated successfully");
      }
    }
    catch (error: unknown) {
      handleError(error instanceof Error ? error : new Error(String(error)));
    }
  };

  return {
    pending,
    mutationErrorMessage,
    addBroadcast,
    updateBroadcast,
  };
}
